import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import { mapBulkPricingTypeToStoreReviewPricingModel } from './storeCheckoutBulkPricingTypeUpdate.utils';
import {
  applyMoqFloorToMinQuantityTiers,
  getStoreCartDrawerMinQuantityTiers,
  getStoreCartPriceFromPriceListOnly,
  isFreshPrintsApparelBrand,
  resolveStoreCartDesignGroupMoqFloor,
} from '@/modules/Cart/StoreCart/helpers/storeCartPricing.utils';
import { getStoreCartDesignGroupKey } from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';
import { hasUsableStoreCartPriceList } from '@/modules/Cart/StoreCart/helpers/storeCartQuote.utils';
import type { IQuoteBreakpoints } from '@/common/interfaces/quoter.interface';

export type StoreReviewPricingModel = 'drops' | 'same';

interface IStoreReviewPricingTierOptions {
  savedBreakpointsOnly?: boolean;
}

export const resolveStoreReviewPricingModelFromGroup = (
  group: DesignGroup
): StoreReviewPricingModel | null => {
  const savedPricingType = group.items
    .map((item) => item.pricingType)
    .find((pricingType) => pricingType != null);

  if (savedPricingType) {
    return mapBulkPricingTypeToStoreReviewPricingModel(savedPricingType);
  }

  return null;
};

export const getDesignGroupTierBrand = (group: DesignGroup): string | undefined =>
  group.items.some((item) => isFreshPrintsApparelBrand(item.apparelBrand))
    ? 'Fresh Prints'
    : group.items[0]?.apparelBrand;

export const getDesignGroupReferencePriceList = (group: DesignGroup): IQuoteBreakpoints[] => {
  const itemWithPriceList = group.items.find((item) => item.priceList?.length);

  return itemWithPriceList?.priceList ?? group.items[0]?.priceList ?? [];
};

/** Infers tiered vs flat pricing from saved cart/store price list data. */
export const inferStoreReviewPricingModelFromGroup = (
  group: DesignGroup
): StoreReviewPricingModel => {
  const savedPricingModel = resolveStoreReviewPricingModelFromGroup(group);

  if (savedPricingModel) {
    return savedPricingModel;
  }

  const brand = getDesignGroupTierBrand(group);
  const priceList = getDesignGroupReferencePriceList(group);

  return hasUsableStoreCartPriceList(priceList, brand) ? 'drops' : 'same';
};

export const getStoreReviewDesignGroupKey = (group: DesignGroup): string | undefined => {
  const firstItem = group.items[0];

  return firstItem ? getStoreCartDesignGroupKey(firstItem) : undefined;
};

export const getStoreReviewDesignGroupQuantityTiers = (
  group: DesignGroup,
  designGroupMoq?: number,
  options: IStoreReviewPricingTierOptions = {}
): number[] => {
  const priceList = getDesignGroupReferencePriceList(group);

  if (options.savedBreakpointsOnly) {
    return priceList.map((breakpoint) => Number(breakpoint.quantity));
  }

  const referenceMinQuant =
    group.items.find((item) => item.minQuant)?.minQuant ?? group.items[0]?.minQuant;
  const moqFloor = resolveStoreCartDesignGroupMoqFloor(designGroupMoq, referenceMinQuant);

  return applyMoqFloorToMinQuantityTiers(getStoreCartDrawerMinQuantityTiers(priceList), moqFloor);
};

/** Quoter-only unit price for checkout — never mock tiers or catalog apparelPrice. */
export const getStoreReviewQuotedItemPrice = (
  quantity: number,
  item: {
    price?: number;
    priceList?: IQuoteBreakpoints[];
    minQuant?: number;
    quantity?: number;
  }
): number | null => {
  const normalizedQuantity = Number(quantity);
  const itemMinimum = Number(item.minQuant ?? item.quantity ?? 0);

  if (typeof item.price === 'number' && item.price > 0 && itemMinimum === normalizedQuantity) {
    return item.price;
  }

  return getStoreCartPriceFromPriceListOnly(normalizedQuantity, item.priceList);
};

export const getStoreReviewItemMinimumQuantity = (item: {
  minQuant?: number;
  quantity?: number;
}): number => Number(item.minQuant ?? item.quantity ?? 0);

/** Exact tier price from priceList — does not floor-match lower quantities. */
export const getStoreReviewExactBreakpointUnitPrice = (
  quantity: number,
  priceList: IQuoteBreakpoints[] | undefined
): number | null => {
  if (!priceList?.length) {
    return null;
  }

  const normalizedQuantity = Number(quantity);
  const exactBreakpoint = priceList.find(
    (breakpoint) => Number(breakpoint.quantity) === normalizedQuantity
  );

  if (exactBreakpoint?.finalPricePerUnit != null && exactBreakpoint.finalPricePerUnit > 0) {
    return exactBreakpoint.finalPricePerUnit;
  }

  return null;
};

/** Display each line at its own cart-quoted minimum (color/product variants can differ). */
export const getStoreReviewItemQuotedPrice = (item: {
  price?: number;
  priceList?: IQuoteBreakpoints[];
  minQuant?: number;
  quantity?: number;
}): number | null => {
  const itemMinimum = getStoreReviewItemMinimumQuantity(item);

  if (!itemMinimum) {
    return null;
  }

  return getStoreReviewQuotedItemPrice(itemMinimum, item);
};

/** Price for a specific breakpoint column — exact quoter tier only (no floor-match). */
export const getStoreReviewBreakpointUnitPrice = (
  quantity: number,
  item: {
    price?: number;
    priceList?: IQuoteBreakpoints[];
    minQuant?: number;
    quantity?: number;
  }
): number | null => {
  const exactPrice = getStoreReviewExactBreakpointUnitPrice(quantity, item.priceList);

  if (exactPrice != null) {
    return exactPrice;
  }

  const normalizedQuantity = Number(quantity);
  const itemMinimum = Number(item.minQuant ?? item.quantity ?? 0);

  if (typeof item.price === 'number' && item.price > 0 && itemMinimum === normalizedQuantity) {
    return item.price;
  }

  return null;
};

export const getStoreReviewPricingBreakpoints = (
  group: DesignGroup,
  designGroupMoq?: number,
  options: IStoreReviewPricingTierOptions = {}
) => {
  const quantities = getStoreReviewDesignGroupQuantityTiers(group, designGroupMoq, options);

  const pricingData = quantities.map((qty) => {
    const prices = group.items.map((item) => getStoreReviewBreakpointUnitPrice(qty, item) ?? 0);

    return { qty, prices };
  });

  return { quantities, pricingData };
};

export const getStoreReviewItemPriceForQuantity = (
  quantity: number,
  item: {
    baseUnitPrice?: number;
    apparelPrice?: number;
    price?: number;
    priceList?: IQuoteBreakpoints[];
    apparelBrand?: string;
    minQuant?: number;
    quantity?: number;
  }
): number => getStoreReviewQuotedItemPrice(quantity, item) ?? 0;

export const needsStoreReviewBreakpointPricing = (
  item: {
    price?: number;
    priceList?: IQuoteBreakpoints[];
    minQuant?: number;
    quantity?: number;
  },
  quantities: number[]
): boolean => {
  const needsPricing = quantities.some(
    (qty) => getStoreReviewExactBreakpointUnitPrice(qty, item.priceList) == null
  );

  // #region agent log
  {
    const exactMissing = quantities.filter(
      (qty) => getStoreReviewExactBreakpointUnitPrice(qty, item.priceList) == null
    );
    const floorResolved = quantities.map((qty) => ({
      qty,
      resolved: getStoreReviewBreakpointUnitPrice(qty, item),
      hasExact: getStoreReviewExactBreakpointUnitPrice(qty, item.priceList) != null,
    }));
    fetch('http://127.0.0.1:7533/ingest/039f2598-1ae5-494b-9857-3e928ecbad58', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': 'bbce4b',
      },
      body: JSON.stringify({
        sessionId: 'bbce4b',
        runId: 'post-fix',
        hypothesisId: 'H-B',
        location: 'storeReviewPricing.utils.ts:needsStoreReviewBreakpointPricing',
        message: 'Checking if breakpoint pricing still needed',
        data: {
          needsPricing,
          quantities,
          priceListLength: item.priceList?.length ?? 0,
          exactMissingQuantities: exactMissing,
          floorResolved,
          wouldNeedIfExactMatch: exactMissing.length > 0,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  }
  // #endregion

  return needsPricing;
};

const getStoreReviewCartItemMinimum = (item: {
  minQuant?: number;
  quantity?: number;
  savedMinimumQuantity?: number;
}): number => Number(item.savedMinimumQuantity ?? item.minQuant ?? item.quantity ?? 0);

export const resolveStoreReviewUnifiedCartMinimum = (
  group: DesignGroup,
  quantities: number[]
): number | null => {
  const itemMinimums = group.items
    .map((item) => getStoreReviewCartItemMinimum(item))
    .filter((minimum) => minimum > 0);

  if (!itemMinimums.length) {
    return null;
  }

  const [sharedMinimum, ...remainingMinimums] = itemMinimums;

  if (!remainingMinimums.every((minimum) => minimum === sharedMinimum)) {
    return null;
  }

  return quantities.includes(sharedMinimum) ? sharedMinimum : null;
};

export const getStoreReviewDefaultMinimumQuantity = (
  group: DesignGroup,
  quantities: number[]
): number => {
  const cartMinQuant =
    group.items.map((item) => getStoreReviewCartItemMinimum(item)).find((minimum) => minimum > 0) ??
    0;

  if (cartMinQuant > 0 && quantities.includes(cartMinQuant)) {
    return cartMinQuant;
  }

  if (quantities.length > 0) {
    return quantities[0];
  }

  return (
    cartMinQuant ??
    getStoreCartDrawerMinQuantityTiers(getDesignGroupReferencePriceList(group))[0] ??
    12
  );
};

export const resolveStoreReviewGroupMinimumQuantity = (
  group: DesignGroup,
  quantities: number[],
  selectedMinimum?: number
): number => {
  if (selectedMinimum != null && quantities.includes(selectedMinimum)) {
    return selectedMinimum;
  }

  return getStoreReviewDefaultMinimumQuantity(group, quantities);
};

/** Only show a previous price when quoter explicitly saved one — not catalog defaults. */
export const resolveStoreReviewPreviousUnitPrice = (
  strikedPricePerUnit: number | undefined | null,
  currentPrice: number,
  apparelPrice?: number
): number | null => {
  if (
    typeof strikedPricePerUnit !== 'number' ||
    strikedPricePerUnit <= 0 ||
    strikedPricePerUnit === currentPrice
  ) {
    return null;
  }

  if (
    typeof apparelPrice === 'number' &&
    apparelPrice > 0 &&
    strikedPricePerUnit === apparelPrice
  ) {
    return null;
  }

  return strikedPricePerUnit;
};
